import { motion } from "framer-motion";
import { IMAGES, EMAIL, PHONE_DISPLAY, waLink, WA_MESSAGES, INSTAGRAM } from "@/lib/site";
import SakuraMark from "@/components/SakuraMark";
import { MessageCircle, Mail, Truck, Instagram } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export const Footer = () => (
    <footer id="contact" className="relative overflow-hidden border-t border-sky-300/70 bg-sakura-200 text-sakura-900" data-testid="footer-contact">
        <div className="relative h-36 w-full overflow-hidden md:h-56">
            <img
                src={IMAGES.petalsWide}
                alt="Cherry blossom petals scattered across a soft surface"
                loading="lazy"
                className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-white/10" />
        </div>

        <div className="relative mx-auto max-w-7xl px-5 pb-10 pt-14 sm:px-8 md:pt-20">
            <div
                aria-hidden="true"
                className="absolute -right-32 top-10 h-80 w-80 rounded-full bg-sky-300/50 blur-3xl"
            />

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="relative"
            >
                <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.32em] text-sakura-700">
                    <span className="inline-block h-2.5 w-4 rotate-45 rounded-full bg-sakura-500" />
                    Say hello
                </p>
                <h2 className="mt-5 max-w-2xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-sakura-900 sm:text-5xl lg:text-6xl">
                    Let's bloom
                    <span className="italic text-sakura-600"> together.</span>
                </h2>

                <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:max-w-3xl">
                    <a
                        data-testid="footer-whatsapp"
                        href={waLink(WA_MESSAGES.general)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-4 rounded-[1.5rem] border border-sakura-400/50 bg-white/80 p-6 backdrop-blur hover:bg-white transition-colors duration-200"
                    >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
                            <MessageCircle size={20} />
                        </span>
                        <span>
                            <span className="block text-[11px] font-bold uppercase tracking-[0.24em] text-sakura-700">
                                WhatsApp
                            </span>
                            <span className="block font-display text-xl font-semibold text-sakura-900">
                                {PHONE_DISPLAY}
                            </span>
                        </span>
                    </a>
                    <a
                        data-testid="footer-email"
                        href={`mailto:${EMAIL}?subject=Order%20enquiry`}
                        className="group flex items-center gap-4 rounded-[1.5rem] border border-sakura-400/50 bg-white/80 p-6 backdrop-blur hover:bg-white transition-colors duration-200"
                    >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
                            <Mail size={20} />
                        </span>
                        <span className="min-w-0">
                            <span className="block text-[11px] font-bold uppercase tracking-[0.24em] text-sakura-700">
                                Email
                            </span>
                            <span className="block truncate font-display text-lg font-semibold text-sakura-900 sm:text-xl">
                                {EMAIL}
                            </span>
                        </span>
                    </a>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                    <a
                        data-testid="footer-whatsapp-cta"
                        href={waLink(WA_MESSAGES.general)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 rounded-full bg-sky-600 px-7 py-4 text-sm font-semibold text-white hover:bg-sky-700 transition-colors duration-200"
                    >
                        <MessageCircle size={18} />
                        Start your order on WhatsApp
                    </a>
                    <a
                        data-testid="footer-instagram"
                        href={INSTAGRAM}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-full border border-sakura-600/40 px-6 py-4 text-sm font-semibold text-sakura-700 hover:bg-sakura-600 hover:text-white transition-colors duration-200"
                    >
                        <Instagram size={17} />
                        @Cherryblossomgift
                    </a>
                    <p className="flex items-center gap-2 text-sm text-sakura-800/85">
                        <Truck size={16} className="text-sakura-700" />
                        Courier cost is not included in prices.
                    </p>
                </div>
            </motion.div>

            <div className="relative mt-16 flex flex-col items-center justify-between gap-4 border-t border-sakura-400/50 pt-8 text-sm text-sakura-800/80 sm:flex-row">
                <p className="flex items-center gap-2">
                    <SakuraMark size={20} />
                    © {new Date().getFullYear()} Cherry Blossom Gifts
                </p>
                <p className="text-sakura-700/80">Handcrafted with love</p>
                <p>Mugs R100 · Mason jars R130 · Nationwide courier</p>
            </div>
        </div>
    </footer>
);

export default Footer;
